import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { LessonResourceType } from 'teacher-ui-client/contentful-client/entry-types'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { MathseedsLessonPreview } from 'teacher-ui-client/interactive-preview/engines'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { LessonPreviewTile } from 'teacher-ui-client/components/tiles/lesson/preview'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { t } from 'ember-intl'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type MathseedsLessonsRoute from 'teacher-ui-client/subscription-type/lessons/mathseeds/previews/route'

interface Args {
  model: ModelFor<MathseedsLessonsRoute>
}

export class MathseedsPreviewsPage extends Component<Args> {
  @service interactivePreview!: InteractivePreviewService

  private readonly resourceOrder: LessonResourceType[] = [
    LessonResourceType.STUDENT_PDF,
    LessonResourceType.TEACHER_PDF,
    LessonResourceType.ES_STUDENT_PDF,
    LessonResourceType.SKILLS,
    LessonResourceType.PROBLEM_SOLVING,
  ]

  get lessonPreviewViewModels() {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    const entries = this.args.model.loaderInstance.value?.lessonsData.items ?? []
    const previews = this.args.model.loaderInstance.value?.previewByLessonDataId ?? {}
    const resources = this.args.model.loaderInstance.value?.resourcesByLessonDataId ?? {}

    return entries.map((entry) => {
      const lessonDataId = entry.sys.id
      const lessonData = entry.fields
      const lessonPreview = previews[lessonDataId]
      const lessonResources = resources[lessonDataId] ?? []
      return {
        id: entry.sys.id,
        title: lessonData.title,
        coverUrl: lessonPreview?.fields.cover?.fields.file?.url,
        openPreview: this.getOpenPreview(lessonData.activityNumber, lessonData.title),
        lessonResources: lessonResources
          .map((resource) => ({
            id: resource.sys.id,
            title: resource.fields.title,
            resourceType: resource.fields.resourceType,
            fileUrl: resource.fields.file?.fields.file?.url,
            videoUrl: resource.fields.videoUrl,
          }))
          .sort((a, b) => this.resourceOrder.indexOf(a.resourceType) - this.resourceOrder.indexOf(b.resourceType)),
        lessonData: {
          activityNumber: lessonData.activityNumber,
        },
      }
    })
  }

  getOpenPreview(activityNumber: number, title: string) {
    return () => {
      const preview = new MathseedsLessonPreview(this, this.interactivePreview.curriculum, String(activityNumber))
      this.interactivePreview.openPreview(preview, { title })
    }
  }

  <template>
    <LessonsPage
      @title={{t "pages.product.lessons.title" product=(t "products.mathseeds")}}
      @loaderInstance={{@model.loaderInstance}}
    >
      <:header>
        <LessonsSearch @themeKey={{ThemeKeys.green}} />
      </:header>
      <:results>
        {{#each this.lessonPreviewViewModels as |viewModel|}}
          <LessonPreviewTile
            data-test-lesson-preview-entry={{viewModel.lessonData.activityNumber}}
            class="w-80"
            @entryId={{viewModel.id}}
            @title={{viewModel.title}}
            @coverUrl={{viewModel.coverUrl}}
            @coverClass="h-40"
            @openPreview={{viewModel.openPreview}}
            @lessonResources={{viewModel.lessonResources}}
          />
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
        {{#if @model.loaderInstance.value.pageCount}}
          <BasePageSelector
            class="basis-full"
            @pageCount={{@model.loaderInstance.value.pageCount}}
            @itemCount={{@model.loaderInstance.value.lessonsData.total}}
          />
        {{/if}}
      </:results>
    </LessonsPage>
  </template>
}

export default RouteTemplate(MathseedsPreviewsPage)
